export type ChromaKeySettings = {
    /** Minimum green channel (0–255) before keying starts */
    threshold: number
    /** Green must exceed red and blue by this factor (e.g. 1.3 → g > r×1.3 && g > b×1.3) */
    greenRatio: number
    /** Width of the soft edge zone in green levels (0–255) */
    spillRange: number
    /** How aggressively to pull green toward avg(r,b) on semi-keyed pixels (0–1) */
    spillStrength: number
    /** Min (g − max(r,b)) to treat as green spill on edges — catches residual halos */
    minGreenExcess: number
}

export const DEFAULT_CHROMA_KEY_SETTINGS: ChromaKeySettings = {
    threshold: 100,
    greenRatio: 1.3,
    spillRange: 40,
    spillStrength: 0.85,
    minGreenExcess: 12,
}

/** 0 = subject, 1 = full green screen — combines ratio+threshold key with excess-green fringe */
function computeGreenScore(r: number, g: number, b: number, settings: ChromaKeySettings): number {
    const { threshold, greenRatio, spillRange, minGreenExcess } = settings
    const maxRB = Math.max(r, b)
    const isGreenDominant = g > r * greenRatio && g > b * greenRatio

    let score = 0

    if (isGreenDominant && g > threshold) {
        score = (g - threshold) / spillRange
    }

    const greenExcess = g - maxRB
    if (greenExcess > minGreenExcess) {
        score = Math.max(score, greenExcess / spillRange)
    }

    return Math.min(1, Math.max(0, score))
}

export type RendererMode = 'webgl' | 'canvas2d' | 'poster'

type ActiveRenderer = {
    mode: RendererMode
    renderFrame: () => void
    updateSettings: (settings: ChromaKeySettings) => void
    destroy: () => void
}

const VERTEX_SHADER = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_texCoord;
void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
}
`

const FRAGMENT_SHADER = `
precision mediump float;
uniform sampler2D u_texture;
uniform float u_threshold;
uniform float u_greenRatio;
uniform float u_spillRange;
uniform float u_spillStrength;
uniform float u_minGreenExcess;
varying vec2 v_texCoord;

void main() {
    vec4 color = texture2D(u_texture, v_texCoord);
    float r = color.r;
    float g = color.g;
    float b = color.b;
    float g255 = g * 255.0;
    float maxRB = max(r, b) * 255.0;

    bool isGreenDominant = g > r * u_greenRatio && g > b * u_greenRatio;

    float greenScore = 0.0;
    if (isGreenDominant && g255 > u_threshold) {
        greenScore = (g255 - u_threshold) / u_spillRange;
    }

    float greenExcess = g255 - maxRB;
    if (greenExcess > u_minGreenExcess) {
        greenScore = max(greenScore, greenExcess / u_spillRange);
    }

    greenScore = clamp(greenScore, 0.0, 1.0);

    if (greenScore <= 0.0) {
        gl_FragColor = color;
        return;
    }

    if (greenScore >= 1.0) {
        gl_FragColor = vec4(0.0);
        return;
    }

    float alpha = 1.0 - greenScore;
    float avgRB = (r + b) * 0.5;
    float despilledG = g - (g - avgRB) * greenScore * u_spillStrength;

    gl_FragColor = vec4(r, despilledG, b, alpha);
}
`

function processPixel(data: Uint8ClampedArray, index: number, settings: ChromaKeySettings): void {
    const r = data[index]
    const g = data[index + 1]
    const b = data[index + 2]
    const greenScore = computeGreenScore(r, g, b, settings)

    if (greenScore <= 0) {
        data[index + 3] = 255
        return
    }

    if (greenScore >= 1) {
        data[index + 3] = 0
        return
    }

    // Spill suppression: fade alpha and pull green toward neutral on transition pixels
    data[index + 3] = Math.round(255 * (1 - greenScore))
    const avgRB = (r + b) / 2
    data[index + 1] = Math.round(g - (g - avgRB) * greenScore * settings.spillStrength)
}

function keyFrameCanvas2d(ctx: CanvasRenderingContext2D, video: HTMLVideoElement, settings: ChromaKeySettings): void {
    const { width, height } = ctx.canvas
    ctx.drawImage(video, 0, 0, width, height)
    const imageData = ctx.getImageData(0, 0, width, height)
    const { data } = imageData

    for (let i = 0; i < data.length; i += 4) {
        processPixel(data, i, settings)
    }

    ctx.putImageData(imageData, 0, 0)
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type)
    if (!shader) return null

    gl.shaderSource(shader, source)
    gl.compileShader(shader)

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader)
        return null
    }

    return shader
}

function createWebGlRenderer(
    canvas: HTMLCanvasElement,
    video: HTMLVideoElement,
    settings: ChromaKeySettings,
): ActiveRenderer | null {
    const gl = canvas.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: false,
        preserveDrawingBuffer: false,
    })

    if (!gl) return null

    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
    if (!vertexShader || !fragmentShader) return null

    const program = gl.createProgram()
    if (!program) return null

    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null

    const positionLocation = gl.getAttribLocation(program, 'a_position')
    const texCoordLocation = gl.getAttribLocation(program, 'a_texCoord')
    const textureLocation = gl.getUniformLocation(program, 'u_texture')
    const thresholdLocation = gl.getUniformLocation(program, 'u_threshold')
    const greenRatioLocation = gl.getUniformLocation(program, 'u_greenRatio')
    const spillRangeLocation = gl.getUniformLocation(program, 'u_spillRange')
    const spillStrengthLocation = gl.getUniformLocation(program, 'u_spillStrength')
    const minGreenExcessLocation = gl.getUniformLocation(program, 'u_minGreenExcess')

    const positionBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)

    const texCoordBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 1, 1, 1, 0, 0, 1, 0]), gl.STATIC_DRAW)

    const texture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

    let currentSettings = { ...settings }

    const uploadUniforms = () => {
        gl.useProgram(program)
        gl.uniform1f(thresholdLocation, currentSettings.threshold)
        gl.uniform1f(greenRatioLocation, currentSettings.greenRatio)
        gl.uniform1f(spillRangeLocation, currentSettings.spillRange)
        gl.uniform1f(spillStrengthLocation, currentSettings.spillStrength)
        gl.uniform1f(minGreenExcessLocation, currentSettings.minGreenExcess)
    }

    const renderFrame = () => {
        if (video.readyState < 2) return

        gl.viewport(0, 0, canvas.width, canvas.height)
        gl.clearColor(0, 0, 0, 0)
        gl.clear(gl.COLOR_BUFFER_BIT)

        gl.useProgram(program)
        uploadUniforms()

        gl.activeTexture(gl.TEXTURE0)
        gl.bindTexture(gl.TEXTURE_2D, texture)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video)
        gl.uniform1i(textureLocation, 0)

        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
        gl.enableVertexAttribArray(positionLocation)
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

        gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer)
        gl.enableVertexAttribArray(texCoordLocation)
        gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0)

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }

    return {
        mode: 'webgl',
        renderFrame,
        updateSettings: next => {
            currentSettings = { ...next }
        },
        destroy: () => {
            gl.deleteProgram(program)
            gl.deleteShader(vertexShader)
            gl.deleteShader(fragmentShader)
            gl.deleteBuffer(positionBuffer)
            gl.deleteBuffer(texCoordBuffer)
            gl.deleteTexture(texture)
        },
    }
}

function createCanvas2dRenderer(
    canvas: HTMLCanvasElement,
    video: HTMLVideoElement,
    settings: ChromaKeySettings,
): ActiveRenderer | null {
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null

    let currentSettings = { ...settings }

    return {
        mode: 'canvas2d',
        renderFrame: () => {
            if (video.readyState < 2) return
            keyFrameCanvas2d(ctx, video, currentSettings)
        },
        updateSettings: next => {
            currentSettings = { ...next }
        },
        destroy: () => {},
    }
}

function benchmarkRenderer(renderer: ActiveRenderer, video: HTMLVideoElement, frames: number): number {
    if (video.readyState < 2) return Infinity

    const start = performance.now()
    for (let i = 0; i < frames; i++) {
        renderer.renderFrame()
    }
    return performance.now() - start
}

function pickRenderer(
    canvas: HTMLCanvasElement,
    video: HTMLVideoElement,
    settings: ChromaKeySettings,
): ActiveRenderer | null {
    const webglRenderer = createWebGlRenderer(canvas, video, settings)
    const canvas2dRenderer = createCanvas2dRenderer(canvas, video, settings)

    if (webglRenderer && canvas2dRenderer && video.readyState >= 2) {
        const webglMs = benchmarkRenderer(webglRenderer, video, 8)
        const canvas2dMs = benchmarkRenderer(canvas2dRenderer, video, 8)

        if (webglMs <= canvas2dMs) {
            canvas2dRenderer.destroy()
            return webglRenderer
        }

        webglRenderer.destroy()
        return canvas2dRenderer
    }

    if (webglRenderer) return webglRenderer
    return canvas2dRenderer
}

export type ChromaKeyPlayer = {
    mode: RendererMode
    setSettings: (settings: Partial<ChromaKeySettings>) => void
    getSettings: () => ChromaKeySettings
    pause: () => void
    resume: () => void
    destroy: () => void
}

export function createChromaKeyPlayer(options: {
    video: HTMLVideoElement
    canvas: HTMLCanvasElement
    settings?: Partial<ChromaKeySettings>
    maxRenderDimension?: number
    onModeSelected?: (mode: RendererMode) => void
    onFirstFrame?: () => void
}): ChromaKeyPlayer {
    const { video, canvas, onModeSelected, onFirstFrame, maxRenderDimension = 720 } = options
    let settings: ChromaKeySettings = { ...DEFAULT_CHROMA_KEY_SETTINGS, ...options.settings }

    const syncCanvasSize = () => {
        let width = video.videoWidth
        let height = video.videoHeight
        if (!width || !height) return false

        const maxSide = Math.max(width, height)
        if (maxRenderDimension > 0 && maxSide > maxRenderDimension) {
            const scale = maxRenderDimension / maxSide
            width = Math.round(width * scale)
            height = Math.round(height * scale)
        }

        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width
            canvas.height = height
        }

        return true
    }

    let renderer: ActiveRenderer | null = null
    let animationId = 0
    let destroyed = false
    let paused = false
    let lastRenderedTime = -1
    let hasNotifiedFirstFrame = false

    const cancelFrame = () => {
        cancelAnimationFrame(animationId)
        animationId = 0
    }

    const scheduleFrame = () => {
        if (destroyed || paused || animationId !== 0) return
        animationId = requestAnimationFrame(tick)
    }

    const tick = () => {
        animationId = 0
        if (destroyed || paused) return

        if (video.readyState >= 2 && syncCanvasSize() && renderer) {
            const hasNewFrame = video.currentTime !== lastRenderedTime || video.seeking
            if (hasNewFrame) {
                renderer.renderFrame()
                lastRenderedTime = video.currentTime
                if (!hasNotifiedFirstFrame) {
                    hasNotifiedFirstFrame = true
                    onFirstFrame?.()
                }
            }
        }

        scheduleFrame()
    }

    const startLoop = () => {
        cancelFrame()
        scheduleFrame()
    }

    const initRenderer = () => {
        if (!syncCanvasSize()) return

        renderer?.destroy()
        const selected = pickRenderer(canvas, video, settings)
        if (!selected) return

        renderer = selected
        onModeSelected?.(selected.mode)
        lastRenderedTime = -1
    }

    const ensurePlayback = () => {
        if (destroyed || paused) return
        if (video.paused) {
            void video.play().catch(() => {})
        }
    }

    const onMetadata = () => {
        initRenderer()
        ensurePlayback()
    }

    const onCanPlay = () => {
        initRenderer()
        ensurePlayback()
    }

    video.addEventListener('loadedmetadata', onMetadata)
    video.addEventListener('canplay', onCanPlay)
    if (video.readyState >= 1) onMetadata()
    if (video.readyState >= 3) onCanPlay()

    void video.play().catch(() => {})
    startLoop()

    return {
        get mode() {
            return renderer?.mode ?? 'poster'
        },
        setSettings: partial => {
            settings = { ...settings, ...partial }
            renderer?.updateSettings(settings)
        },
        getSettings: () => ({ ...settings }),
        pause: () => {
            paused = true
            cancelFrame()
            video.pause()
        },
        resume: () => {
            if (destroyed) return
            paused = false
            lastRenderedTime = -1
            void video.play().catch(() => {})
            startLoop()
        },
        destroy: () => {
            destroyed = true
            paused = true
            cancelFrame()
            video.removeEventListener('loadedmetadata', onMetadata)
            video.removeEventListener('canplay', onCanPlay)
            renderer?.destroy()
            renderer = null
        },
    }
}

export function isChromaKeySupported(): boolean {
    if (typeof document === 'undefined') return false
    const canvas = document.createElement('canvas')
    return Boolean(canvas.getContext('2d') || canvas.getContext('webgl'))
}
